import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import {
  Inbox, Clock, Loader2, Building2, Sparkles,
  CheckCircle, XCircle, Eye, TrendingUp, ArrowUpRight
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  loading: boolean;
}

const StatCard = ({ title, value, subtitle, icon: Icon, color, loading }: StatCardProps) => (
  <div className="bg-[#18181b] rounded-xl border border-white/5 p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-zinc-500 mb-1">{title}</p>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-zinc-600" />
        ) : (
          <p className="text-2xl font-bold text-white">{value}</p>
        )}
        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

export default function MerchantDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState<unknown>(null);
  const [recentPitches, setRecentPitches] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pitchesRes] = await Promise.all([
        fetch(getApiUrl('/api/v2/merchant/engagement/dashboard'), { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(getApiUrl('/api/v2/merchant/engagement/pitches?limit=5'), { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (pitchesRes.ok) setRecentPitches(await pitchesRes.json());
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const STATUS_BADGE: Record<string, { color: string; icon: React.ComponentType<{ className?: string }> }> = {
    VALUABLE: { color: 'bg-emerald-500/10 text-emerald-400', icon: Sparkles },
    DELIVERED: { color: 'bg-blue-500/10 text-blue-400', icon: Eye },
    ACCEPTED: { color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle },
    REJECTED: { color: 'bg-rose-500/10 text-rose-400', icon: XCircle },
  };

  const statsObj = stats as { total_valuable?: number; pending_review?: number; total_accepted?: number; new_this_week?: number } | null;
  const recentList = recentPitches as Array<{ id: string; product_name?: string; vendor_name?: string; status_v2?: string }>;

  return (
    <MerchantLayout>
      <div className="space-y-6" data-testid="merchant-dashboard">
        {/* Welcome */}
        <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 rounded-2xl p-6 border border-emerald-500/20">
          <h1 className="text-2xl font-bold text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'Merchant'}!
          </h1>
          <p className="text-zinc-400">
            Review evaluated vendor pitches and discover opportunities for your business.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard title="Valuable" value={statsObj?.total_valuable ?? 0} subtitle="Awaiting review" icon={Sparkles} color="bg-emerald-500/10 text-emerald-400" loading={loading} />
          <StatCard title="Delivered" value={statsObj?.pending_review ?? 0} subtitle="Needs decision" icon={Eye} color="bg-blue-500/10 text-blue-400" loading={loading} />
          <StatCard title="Accepted" value={statsObj?.total_accepted ?? 0} icon={CheckCircle} color="bg-emerald-500/10 text-emerald-400" loading={loading} />
          <StatCard title="New This Week" value={statsObj?.new_this_week ?? 0} icon={Clock} color="bg-rose-500/10 text-rose-400" loading={loading} />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Pitches */}
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Recent Pitches</h2>
              {recentList.length > 0 && (
                <Link to="/merchant/recommendations" className="text-sm text-rose-400 hover:text-rose-300 font-medium flex items-center gap-1">
                  View all <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
            {loading ? (
              <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-zinc-600 mx-auto" /></div>
            ) : recentList.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                <p>No pitches yet</p>
                <p className="text-sm">Evaluated pitches will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {recentList.map((pitch) => {
                  const badge = STATUS_BADGE[pitch.status_v2 ?? ''] || STATUS_BADGE.VALUABLE;
                  const BadgeIcon = badge.icon;
                  return (
                    <Link
                      key={pitch.id}
                      to={`/merchant/pitches/${pitch.id}`}
                      className="flex items-center gap-3 py-3 hover:bg-white/5 -mx-2 px-2 rounded-lg transition-colors"
                      data-testid={`recent-pitch-${pitch.id}`}
                    >
                      <div className="p-2 rounded-lg bg-emerald-500/10">
                        <Building2 className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{pitch.product_name}</p>
                        <p className="text-sm text-zinc-500 truncate">{pitch.vendor_name}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                        <BadgeIcon className="w-3 h-3" />
                        {pitch.status_v2}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
            <h2 className="text-base font-semibold text-white mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link to="/merchant/recommendations" data-testid="quick-action-recommendations"
                className="flex items-center gap-4 p-4 rounded-lg border border-white/5 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-colors group">
                <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-400 group-hover:bg-emerald-500/20">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-white">View Recommendations</p>
                  <p className="text-sm text-zinc-500">See top-scoring valuable pitches</p>
                </div>
              </Link>
              <Link to="/merchant/pitches" data-testid="quick-action-inbox"
                className="flex items-center gap-4 p-4 rounded-lg border border-white/5 hover:border-white/10 hover:bg-white/5 transition-colors group">
                <div className="p-3 rounded-lg bg-white/5 text-zinc-400 group-hover:bg-white/10">
                  <Inbox className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-white">View All Pitches</p>
                  <p className="text-sm text-zinc-500">Browse all evaluated vendor pitches</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MerchantLayout>
  );
}
